import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ViewEncapsulation,
  TemplateRef,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  signal,
  WritableSignal,
  effect,
  untracked,
  inject,
  DestroyRef
} from '@angular/core';
import { AssetVersionPipe } from '../../../pipes/asset-version.pipe';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  NgSelectComponent,
  NgHeaderTemplateDirective,
  NgOptionTemplateDirective,
  NgFooterTemplateDirective,
  NgOptgroupTemplateDirective,
  NgPlaceholderTemplateDirective
} from '@ng-select/ng-select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum SelectMode {
  MULTI,
  SINGLE,
  ATTACHED_SINGLE,
  ATTACHED_MULTI
}

export interface SelectItem<T = string> {
  label: string;
  key: T;
  disabled?: boolean;
  icon?: string;
  group?: { label: string; order: number };
  // Short muted text under the label
  hint?: string;
  tooltip?: string;
  // Lower values sort first, inside a group and before the label
  sortOrder?: number;
}

enum TemplateType {
  PLACEHOLDER,
  MULTI_LABEL,
  SINGLE_LABEL
}

interface TemplateContext {
  text: string;
  showIcon: boolean;
  iconPath?: string;
  iconStyle?: { [key: string]: any };
  containerClass: { [k: string]: boolean };
  textClass: string;
}

type SelectValue<T, M> = (M extends (SelectMode.MULTI | SelectMode.ATTACHED_MULTI) ? T[] | null : T | null);


@Component({
  selector: 'app-select-menu',
  standalone: true,
  imports: [
    CommonModule,
    NgSelectComponent,
    FormsModule,
    ReactiveFormsModule,
    NgHeaderTemplateDirective,
    NgOptionTemplateDirective,
    NgFooterTemplateDirective,
    NgOptgroupTemplateDirective,
    NgPlaceholderTemplateDirective,
    AssetVersionPipe
  ],
  templateUrl: './select-menu.component.html',
  styleUrl: './select-menu.component.scss',
  encapsulation: ViewEncapsulation.None
})


export class SelectMenuComponent<T = string, M extends SelectMode = SelectMode.SINGLE>
  implements OnInit, AfterViewInit, OnChanges {

  @Input({ required: true }) selectMode!: M;
  @Input({ required: true }) control!: FormControl<T | T[] | null>;
  @Input({ required: true }) items!: SelectItem<T>[];
  @Input() disabled = false;
  @Input() width = '100%';
  @Input() placeholder = 'Select...';
  @Input() iconPath?: string;
  @Input() iconStyle?: { [key: string]: any } = {};
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Search item';
  @Input() showPlaceholderIcon = false;
  @Input() hideSelected = false;
  @Input() selectAllAndClearOptions = false;
  @Input() showAllSelected = false;
  @Input() showSelectedIcon = false;
  @Input() showPlaceholderAllSelected = false;
  @Input() atLeastOneSelected = false;
  @Input() compareWith?: (option1: any, option2: any) => boolean;
  @Input() openPanel: WritableSignal<any> = signal(null);
  // 'auto' opens downward when there's room and flips up near the viewport bottom, so a body-appended
  // panel's footer (e.g. a multi-select's Apply) never renders off-screen. The default stays 'bottom' —
  // the historical behavior every non-drawer consumer is built around; portal users opt into 'auto'.
  @Input() dropDownPosition: 'bottom' | 'top' | 'auto' = 'bottom';
  // Opt-in portal: 'body' appends the panel to <body>, so it can never be clipped by a scrollable/
  // overflow ancestor or trapped under a stacking context — the management drawers opt in because
  // their scrollable body would otherwise clip the dropdowns. ng-select copies this component's
  // ngClass onto the appended panel so it keeps the `.menu-select-ng-select` styling (incl. the
  // above-overlay z-index); the SCSS scopes trigger-only geometry away from the panel; and the panel
  // closes on outside scroll (see onOpen/close) so it never detaches from its trigger.
  // Deliberately defaults to '' (inline): a body portal moves the options OUT of the trigger's DOM
  // subtree, which breaks anything that scopes option lookups inside the component — the E2E filter
  // suites do exactly that (getByTestId('...-filter').getByText(option)). effectiveAppendTo keeps
  // attached + wide-width selects inline regardless — they position relative to the trigger and
  // portaling them would only break their layout.
  @Input() appendTo = '';
  @Input() dropDownSide: 'left' | 'right' = 'right';
  @Input() dropDownWideWidth = false;
  @Input() customHeaderTemplate?: TemplateRef<any>;
  @Input() limitedHeight = false;
  @Input() showAllSelectedIcons = false;
  @Input() iconShape = 'default'; // use 'flag' for rectangle shape
  @Input() maxIconsAmount = 18;
  @Input() hasError = false;
  @Input() virtualScroll = false;
  @Input() arrowIcon = 'svg/sort-arrow.svg';
  @Input() maxSelectedItems?: number;

  @Output() onApply = new EventEmitter<SelectValue<T, M>>();

  @ViewChild('ngSelect') readonly ngSelect!: NgSelectComponent;
  @ViewChild('multiLabel') multiLabel!: TemplateRef<any>;
  @ViewChild('singleLabel', { static: true }) singleLabel!: TemplateRef<any>;
  @ViewChild('placeholderTmp') placeholderTmp!: TemplateRef<any>;

  public filteredItems$!: WritableSignal<SelectItem<T>[]>;
  public previewsValue: SelectItem<T>[] = [];

  protected readonly TemplateType = TemplateType;
  protected readonly SelectMode = SelectMode;
  protected searchTerm = '';

  protected _tempSelected: SelectItem<T>[] | SelectItem<T> | null = null;
  protected _multiSelectedText = '';
  protected _currentTemplate!: TemplateRef<any>;

  private destroy$ = inject(DestroyRef);
  private elementRef = inject(ElementRef);
  private multiSelectedItems: SelectItem<T>[] = [];
  private singleSelectedItems: SelectItem<T> | null = null;
  private scrollCloseRegistered = false;
  private keyNavRegistered = false;
  // Handle for onOpen's deferred setup, so a close/destroy inside the same tick cancels it — an
  // uncancelled callback would register a window scroll listener after cleanup already ran.
  private openTimer?: ReturnType<typeof setTimeout>;

  public isOpen: boolean = false;

  // Closes the dropdown when the user scrolls anywhere outside the panel — the canonical behaviour for
  // a body-appended overlay, which would otherwise detach from its trigger. Scrolls inside the panel's
  // own option list are ignored. Capture phase is required because scroll events don't bubble.
  private readonly onOutsideScroll = (event: Event): void => {
    const panel = document.querySelector('.ng-dropdown-panel.menu-select-ng-select');
    const target = event.target as Node | null;
    if (panel && target && panel.contains(target)) {
      return;
    }
    this.ngSelect?.close();
  };

  // Keyboard navigation, owned here rather than by ng-select: when the panel is appended to <body> the
  // focused element (the search field) lives outside ng-select's DOM, so its native keydown host
  // listener never fires. Capture phase + stopPropagation means this is also the single source of truth
  // for inline panels (ng-select doesn't double-handle). ↑/↓ move the highlight, Enter applies (multi)
  // or selects (single), Esc cancels. Space is intentionally NOT handled: it's plain text for the search
  // field and never doubles as a selection action — options are selected by mouse click or Enter only.
  private readonly onKeyNav = (event: KeyboardEvent): void => {
    if (!this.isOpen) {
      return;
    }
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        // Delegate arrows to ng-select: it moves the highlight AND scrolls its own panel list into
        // view (virtual-scroll aware). Crucially that scroll stays inside the panel, so it doesn't
        // trip the close-on-outside-scroll listener. stopPropagation prevents the (inline) host
        // listener from double-handling.
        this.ngSelect?.handleKeyDown(event);
        event.preventDefault();
        event.stopPropagation();
        break;
      case 'Enter':
        this.consume(event);
        this.isMultiSelect ? this.close(true) : this.activateMarkedOption();
        break;
      case 'Escape':
      case 'Esc':
        this.consume(event);
        this.close();
        break;
    }
  };

  constructor(private cdr: ChangeDetectorRef) {
    this.destroy$.onDestroy(() => {
      clearTimeout(this.openTimer);
      this.removeScrollCloseListener();
      this.removeKeyNavListener();
    });
    effect(() => {
      const shouldOpen = this.openPanel();
      untracked(() => {
        if (shouldOpen) {
          this.ngSelect.open();
        } else {
          this.close(false, false);
        }
      });
    });
  }

  ngOnInit(): void {
    this.validateInput();
    this.filteredItems$ = signal<SelectItem<T>[]>(this.toDisplayItems(this.items));
    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroy$))
      .subscribe(() => this.setValueFromControl());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.filteredItems$ = signal<SelectItem<T>[]>(this.toDisplayItems(this.items));
      this.setValueFromControl();
    }
  }

  ngAfterViewInit(): void {
    this.updateDynamicTemplate();
    this.cdr.detectChanges();
  }



  public filterItems(term: string = ''): void {
    this.searchTerm = term;
    const matchedItems = term
      ? this.items.filter((item: SelectItem<T>) =>
        item.label.toLowerCase().includes(term.toLowerCase())
      )
      : this.items;

    // Grouping the matches keeps the ordering the panel shows on open as the user types, instead of
    // collapsing to a flat, unordered list.
    this.filteredItems$.set(this.toDisplayItems(matchedItems));
  }

  public selectAll(): void {
    this._tempSelected = this.filteredItems$();
  }

  public clearAll(): void {
    this._tempSelected = [];
  }

  public apply(valueFromControl: boolean = false): void {
    this.filteredItems$.set(this.toDisplayItems(this.items));
    this.selected = this._tempSelected;
    this.previewsValue = this.selected as SelectItem<T>[];

    if (this.isMultiSelect) {
      this._multiSelectedText = this.multiSelectedText;
    }
    if (!valueFromControl) {
      this.setControlValue();
      this.onApply.emit(this.getSelectedKeys());
    }
    this.updateDynamicTemplate();
  }

  public onCancel(): void {
    this.searchTerm = '';
    if (this.isMultiSelect) {
      this._tempSelected = this.previewsValue;
    }
  }

  protected groupFn(items: SelectItem<T>): string | undefined {
    return items.group?.label;
  }

  protected get isMultiSelect(): boolean {
    return this.selectMode === SelectMode.MULTI || this.selectMode === SelectMode.ATTACHED_MULTI;
  }

  protected get isAttachedSelect(): boolean {
    return this.selectMode === SelectMode.ATTACHED_SINGLE || this.selectMode === SelectMode.ATTACHED_MULTI;
  }

  // Attached selects anchor to a 0-size trigger, and wide-width selects size/position relative to the
  // trigger inline — both misbehave when portaled to <body>, and neither risks overflow clipping, so
  // they stay inline regardless of the appendTo input.
  protected get effectiveAppendTo(): string {
    return this.isAttachedSelect || this.dropDownWideWidth ? '' : this.appendTo;
  }

  // ng-select decides which options are selected by matching the model against the option list. It
  // falls back to object identity (then label equality) when given no compareWith, and identity can't
  // hold here: groupAndSort rebuilds items as new objects, so a selected-first multi-select never
  // renders the same references it was handed. `key` is what identifies an item everywhere else in
  // this component (getSelectedKeys, setControlValue, groupAndSort), so match on it and don't leave
  // selection state resting on labels being unique. Must stay a stable reference — a getter returning
  // a fresh arrow would report a changed binding on every cycle. Wrapper components bind their own
  // undefined compareWith through to us, which is why the fallback lives here and not in a field
  // initializer: an explicit undefined binding would overwrite it.
  private readonly compareByKey = (option1: any, option2: any): boolean => option1?.key === option2?.key;

  protected get effectiveCompareWith(): (option1: any, option2: any) => boolean {
    return this.compareWith ?? this.compareByKey;
  }

  protected get selectedAmount(): number {
    return (this.previewsValue as SelectItem<T>[]).length - 1;
  }

  protected get multiSelectedText(): string {
    return (
      this.selectedText +
      (this.selectedAmount && !this.showAllSelected
        ? ` + ${this.selectedAmount} `
        : '')
    );
  }

  protected get singleSelectedText(): string {
    return (
      this.items.find((item) => item === (this.selected as SelectItem<T>))?.label ??
      ''
    );
  }

  protected get selected(): SelectItem<T>[] | SelectItem<T> | null {
    return this.isMultiSelect ? this.multiSelectedItems : this.singleSelectedItems;
  }

  protected set selected(item: SelectItem<T> | SelectItem<T>[] | null) {
    if (this.isMultiSelect) {
      this.multiSelectedItems = item as SelectItem<T>[];
    } else {
      this.singleSelectedItems = item as SelectItem<T>;
    }
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = new AssetVersionPipe().transform('images/no-logo.png');
  }

  protected getTemplateContext(
    templateType: TemplateType,
    item?: SelectItem<T> | SelectItem<T>[] | null
  ): TemplateContext {
    switch (templateType) {
      case TemplateType.PLACEHOLDER:
        return {
          text: this.placeholder,
          showIcon: !!this.iconPath,
          iconPath: this.iconPath,
          containerClass: { 'placeholder-container': true, 'disabled': this.disabled },
          textClass: 'placeholder'
        };
      case TemplateType.MULTI_LABEL:
        return {
          text: this._multiSelectedText,
          showIcon: this.showPlaceholderIcon && !!this.iconPath,
          iconPath: this.iconPath,
          containerClass: { 'multi-value-container': true, 'disabled': this.disabled },
          textClass: 'multi-value'
        };
      case TemplateType.SINGLE_LABEL:
        item = item as SelectItem<T> | null;
        return {
          text: this.singleSelectedText,
          showIcon: this.showPlaceholderIcon || this.showSelectedIcon,
          iconPath: this.showPlaceholderIcon ? this.iconPath : item?.icon,
          iconStyle: this.showSelectedIcon ? this.iconStyle : undefined,
          containerClass: { 'single-value-container': true, 'disabled': this.disabled },
          textClass: 'single-value'
        };
    }
  }

  public close(isApply: boolean = false, callClose: boolean = true): void {
    isApply && this._tempSelected ? this.apply() : this.onCancel();
    if (this.ngSelect && callClose) {
      this.ngSelect.close();
    }
    this.isOpen = false;
    clearTimeout(this.openTimer);
    this.removeScrollCloseListener();
    this.removeKeyNavListener();
  }

  protected onOpen(): void {
    this.isOpen = true;
    this.addKeyNavListener();
    // Defer to the next tick so ng-select has stamped/appended the panel and any open-time scroll
    // adjustment has settled before we wire up focus + the close-on-scroll listener.
    this.openTimer = setTimeout(() => {
      if (this.effectiveAppendTo) {
        this.addScrollCloseListener();
      }
      if (this.searchable) {
        const host = this.elementRef.nativeElement as HTMLElement;
        const input = host.querySelector<HTMLInputElement>('.search-input')
          ?? document.querySelector<HTMLInputElement>('.ng-dropdown-panel.menu-select-ng-select .search-input');
        input?.focus();
      }
    });
  }

  private addScrollCloseListener(): void {
    if (this.scrollCloseRegistered) {
      return;
    }
    window.addEventListener('scroll', this.onOutsideScroll, true);
    this.scrollCloseRegistered = true;
  }

  private removeScrollCloseListener(): void {
    if (!this.scrollCloseRegistered) {
      return;
    }
    window.removeEventListener('scroll', this.onOutsideScroll, true);
    this.scrollCloseRegistered = false;
  }

  private addKeyNavListener(): void {
    if (this.keyNavRegistered) {
      return;
    }
    // Capture phase so this runs before ng-select's own (bubble) keydown host listener and the search
    // input's default handling, letting us fully own ↑/↓/Space/Enter/Esc.
    document.addEventListener('keydown', this.onKeyNav, true);
    this.keyNavRegistered = true;
  }

  private removeKeyNavListener(): void {
    if (!this.keyNavRegistered) {
      return;
    }
    document.removeEventListener('keydown', this.onKeyNav, true);
    this.keyNavRegistered = false;
  }

  private consume(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private getPanelElement(): HTMLElement | null {
    // appended panels live in <body>; inline panels live inside the component host.
    return document.querySelector<HTMLElement>('.ng-dropdown-panel.menu-select-ng-select')
      ?? (this.elementRef.nativeElement as HTMLElement).querySelector<HTMLElement>('.ng-dropdown-panel');
  }

  // Clicking the highlighted option mirrors a mouse click exactly — toggle it (multi) or select it and
  // close (single) — so the selection model and Apply flow stay identical across mouse and keyboard.
  private activateMarkedOption(): void {
    this.getPanelElement()?.querySelector<HTMLElement>('.ng-option-marked')?.click();
  }

  handleItemClick(item: any, item$: any): void {
    if (this.selectMode === SelectMode.ATTACHED_SINGLE && item$?.selected) {
      this._tempSelected = item;
      this.close(true);
    }
  }

  protected singleSelect(): void {
    if (!this.isMultiSelect) {
      this.close(true);
    }
  }

  protected get exceedsMaxSelected(): boolean {
    return !!(this.maxSelectedItems && this.isMultiSelect && Array.isArray(this._tempSelected) && this._tempSelected.length > this.maxSelectedItems);
  }

  protected get disableApply(): boolean {
    if (this.exceedsMaxSelected) return true;
    return this.atLeastOneSelected && (this.isMultiSelect ? !(this._tempSelected as T[]).length : !!this._tempSelected);
  }

  private setControlValue(): void {
    const selectedKey = this.isMultiSelect
      ? (this.selected as SelectItem[]).map((item) => item.key) as T[]
      : (this.selected as SelectItem | null)?.key as T ?? null;

    this.control.setValue(selectedKey as SelectValue<T, M>);
  }

  private getGroupLabel(isSelected: boolean): string {
    return isSelected ? 'Selected Item' : 'Not Selected';
  }

  // Single source of truth for what the panel renders: multi-selects always group their selection to
  // the top (DEV-591), single-selects have no groups and keep the order they were handed. Every path
  // that refreshes filteredItems$ — init, an items change, search, apply — goes through here.
  private toDisplayItems(items: SelectItem<T>[]): SelectItem<T>[] {
    return this.isMultiSelect ? this.groupAndSort(items) : items;
  }

  private groupAndSort(items: SelectItem<T>[]): SelectItem<T>[] {
    const selectedItems: SelectValue<T, M> = this._tempSelected as SelectValue<T, M>;
    const isArray = Array.isArray(selectedItems)
    return items
      .map((item) => {
        let isSelected: boolean = false
        if (isArray) {
          isSelected = selectedItems?.some(i => i.key === item.key);
        }
        return {
          // Spread rather than pick: this runs for every multi-select now, so dropping fields here
          // (e.g. an item's `disabled`) would silently break any consumer that sets them.
          ...item,
          ...(isArray && selectedItems.length
            ? {
              group: {
                label: this.getGroupLabel(isSelected),
                order: +!isSelected
              }
            }
            : {})
        };
      })
      .sort((a, b) => {
        const groupA = a.group?.order ?? Infinity;
        const groupB = b.group?.order ?? Infinity;

        if (groupA !== groupB) {
          return groupA - groupB;
        }
        if ((a.sortOrder ?? 0) !== (b.sortOrder ?? 0)) {
          return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
        }
        if (a.label.toLowerCase() === "other") {
          return 1;
        }
        if (b.label.toLowerCase() === "other") {
          return -1;
        }
        return a.label.localeCompare(b.label);
      });
  }

  private get selectedText(): string {
    const selectedItems = this.previewsValue as SelectItem<T>[];
    return !selectedItems.length
      ? ''
      : this.showAllSelected
        ? selectedItems.map((item) => item.label).join(', ')
        : selectedItems[0].label;
  }

  private validateInput(): void {
    const isMulti = this.isMultiSelect;

    this.selectAllAndClearOptions = isMulti && this.selectAllAndClearOptions;
    this.showAllSelected = isMulti && this.showAllSelected;
    this.showSelectedIcon = !isMulti && this.showSelectedIcon;
    this.hideSelected = !isMulti && this.hideSelected;
  }

  protected updateDynamicTemplate(): void {
    let templateToRender: TemplateRef<any>;
    if (this.isMultiSelect) {
      if (this.previewsValue.length > 0) {
        templateToRender =
          this.showPlaceholderAllSelected &&
            this.previewsValue.length === this.items.length
            ? this.placeholderTmp
            : this.multiLabel;
      } else {
        templateToRender = this.placeholderTmp;
      }
    } else {
      templateToRender = this.selected ? this.singleLabel : this.placeholderTmp;
    }
    this._currentTemplate = templateToRender;
  }

  private setValueFromControl(): void {
    this._tempSelected =
      (this.isMultiSelect
        ? this.items.filter(({ key }) =>
          (this.control.value as T[]).includes(key)
        )
        : this.items.find(({ key }) => key === this.control.value)) ??
      null;

    this.apply(true);
  }

  private getSelectedKeys(): SelectValue<T, M> {
    return this.isMultiSelect
      ? (this.selected as SelectItem<T>[]).map((item) => item.key) as SelectValue<T, M>
      : (this.selected as SelectItem<T>).key as SelectValue<T, M>;
  }
}
